import type {
  BusinessLoginResponse,
  BusinessDashboard,
  BusinessUserProfile,
  BusinessOrderSummary,
  BusinessOrderDetail,
  BusinessAddress,
  BusinessUserEntry,
  BusinessCreditOverview,
  BusinessInvoice,
  BusinessCheckoutResult,
  BusinessStats,
  B2BProductSearchResult,
  B2BProductDetail,
  BusinessVehicle,
  BusinessVehicleListItem,
  BusinessVehicleDetail,
  BusinessVehicleNote,
  BusinessVehicleNoteProduct,
  BusinessVehicleOrderer,
  BusinessVehicleOrder,
  BusinessMechanic,
  BusinessVehicleMechanic,
  BusinessVehicleImage,
  BusinessVehicleHistory,
  BusinessCreditNote,
  BusinessAuditEntry,
  BusinessCreditRequestsResponse,
  CarTermsStatus,
  B2BCar,
  B2BCarDetail,
  B2BCarBid,
  B2BCarOrder,
  InsuranceClaimSummary,
  InsuranceClaimDetail,
  InsuranceClaimItem,
  InsuranceClaimLabor,
  InsuranceClaimDocument,
  InsuranceStatusHistoryEntry,
  InsuranceApprovedWorkshop,
  InsuranceBillingProfile,
  InsuranceLaborInvoice,
  InsuranceStats,
  InsuranceWorkshopSearchResult,
  InsuranceReportData,
  InsuranceWorkshopInvitation,
  PartnerListingItem,
  PartnerListingDetail,
  PartnerListingsResponse,
  PartnerBidResponse,
  PartnerMyBid,
} from '@/types/business';

export interface PortalMe {
  user_id: number; namn: string; roll: string; roll_namn: string; attest_grans: number | null;
  behorigheter: Record<'skapa' | 'andra_alla' | 'andra_egna' | 'fordela' | 'verkstader' | 'fakturering' | 'fakturering_lasa' | 'anvandare' | 'logg', boolean>;
}
export interface PortalTeamMedlem {
  id: number; email: string; namn: string; firstname: string; lastname: string; phone: string | null; roll: string; roll_namn: string;
  attest_grans: number | null; egen_grans: boolean; active: boolean; last_login: string | null; oppna_arenden: number;
}
export interface PortalLoggRad {
  id: number; created_at: string; action: string; description: string; entity_id: number | null; amount: string | null;
  anvandare: string | null; epost: string | null; roll: string | null; ip: string | null; claim_number: string | null; entry_hash: string;
}

const API_URL = process.env.NEXT_PUBLIC_API_URL || 'https://api.autofrance.se/api';

/** En faktura som den står i Fortnox — den kunden faktiskt fått (2026-10-08). */
export interface FortnoxFaktura {
  nr: number; datum: string; forfaller: string | null; total: number; saldo: number; valuta: string; ocr: string | null;
  /** Fortnox FinalPayDate — satt när fakturan är helt betald. */
  betald_datum?: string | null;
  kredit: boolean; krediterad_av: number | null; status: 'betald' | 'obetald' | 'forfallen' | 'delvis_betald' | 'kredit';
  af_nummer: string | null;
}

/** Höjs när skissbilderna ändras (maskning, beskärning) — webbläsaren hade annars kvar en gammal bild i upp till en timme. */
const SKISS_BILD_VERSION = 5;
export interface SkissRad { nr: number; text: string; rubrik: boolean; pos: string | null; villkor: string | null; ejForBilen: boolean; t: string | null; del: boolean; produkter: number[] }
export interface SkissVy { brodsmulor: Array<{ namn: string; t: string | null }>; rader: SkissRad[]; bilder: Array<{ t: string }>; sok: string | null; nasta: string | null }
export interface ForsakringAktivering { aktiv: boolean; aktiv_at: string | null; aktiv_av: string | null; kan_andra: boolean }

class BusinessAPI {
  private getToken(): string | null {
    if (typeof window === 'undefined') return null;
    try {
      const stored = localStorage.getItem('autofrance-business-auth');
      if (stored) {
        const parsed = JSON.parse(stored);
        return parsed.state?.token || null;
      }
    } catch {
      return null;
    }
    return null;
  }

  private async fetch<T>(path: string, options?: RequestInit): Promise<T> {
    const token = this.getToken();
    const res = await fetch(`${API_URL}${path}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...options?.headers,
      },
    });

    if (!res.ok) {
      const error = await res.json().catch(() => ({ message: 'Okänt fel' }));
      // Only auto-redirect on 401 for authenticated requests (not login/register)
      if (res.status === 401 && !path.includes('/login') && !path.includes('/register') && !path.includes('/apply')) {
        localStorage.removeItem('autofrance-business-auth');
        window.location.href = '/foretag/login';
      }
      throw new Error(error.message || `API-fel: ${res.status}`);
    }

    return res.json();
  }

  // Auth (no token needed)
  async apply(data: Record<string, unknown>) {
    return this.fetch<{ success: boolean; message: string }>('/business/apply', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async clearSavedCart() {
    return this.fetch<{ success: boolean }>('/business/saved-cart', { method: 'DELETE' });
  }
  async checkApplicationStatus(org_number: string, email: string) {
    const params = new URLSearchParams({ org_number, email });
    return this.fetch<{ status: string; company_name: string; rejected_reason: string | null; applied_at: string }>(
      `/business/apply/status?${params}`
    );
  }

  async login(email: string, password: string) {
    return this.fetch<BusinessLoginResponse>('/business/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
  }

  async forgotPassword(email: string) {
    return this.fetch<{ success: boolean; message: string }>('/business/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }

  async resetPassword(token: string, password: string) {
    return this.fetch<{ success: boolean; message: string }>('/business/reset-password', {
      method: 'POST',
      body: JSON.stringify({ token, password }),
    });
  }

  // Dashboard
  async getProfile() {
    return this.fetch<BusinessDashboard>('/business/me');
  }

  async updateProfile(data: { firstname?: string; lastname?: string; phone?: string }) {
    return this.fetch<{ user: BusinessUserProfile }>('/business/me', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async changePassword(current_password: string, new_password: string) {
    return this.fetch<{ success: boolean; message: string }>('/business/me/change-password', {
      method: 'POST',
      body: JSON.stringify({ current_password, new_password }),
    });
  }

  // Users
  async getUsers() {
    return this.fetch<{ users: BusinessUserEntry[] }>('/business/users');
  }

  async createUser(data: { email: string; password: string; firstname: string; lastname: string; phone?: string; role?: string; is_mechanic?: boolean; permissions?: Record<string, boolean>; attest_grans?: number | null; price_visibility?: string }) {
    return this.fetch<{ user: BusinessUserEntry }>('/business/users', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateUser(id: number, data: Partial<{ firstname: string; lastname: string; phone: string; role: string; active: boolean; permissions: Record<string, boolean>; is_mechanic: boolean; price_visibility: string; attest_grans: number | null }>) {
    return this.fetch<{ user: BusinessUserEntry }>(`/business/users/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deactivateUser(id: number) {
    return this.fetch<{ success: boolean }>(`/business/users/${id}`, { method: 'DELETE' });
  }

  async permanentDeleteUser(id: number) {
    return this.fetch<{ success: boolean }>(`/business/users/${id}/delete`, { method: 'POST' });
  }

  // Addresses
  async getAddresses() {
    return this.fetch<{ addresses: BusinessAddress[] }>('/business/addresses');
  }

  async createAddress(data: Omit<BusinessAddress, 'id' | 'business_customer_id' | 'created_at'>) {
    return this.fetch<{ address: BusinessAddress }>('/business/addresses', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateAddress(id: number, data: Partial<BusinessAddress>) {
    return this.fetch<{ address: BusinessAddress }>(`/business/addresses/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteAddress(id: number) {
    return this.fetch<{ success: boolean }>(`/business/addresses/${id}`, { method: 'DELETE' });
  }

  // Orders
  async getOrders(params?: { page?: number; status?: string }) {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set('page', String(params.page));
    if (params?.status) searchParams.set('status', params.status);
    const query = searchParams.toString();
    return this.fetch<{ data: BusinessOrderSummary[]; total: number; page: number; total_pages: number }>(
      `/business/orders${query ? `?${query}` : ''}`
    );
  }

  async getOrder(id: number) {
    return this.fetch<BusinessOrderDetail>(`/business/orders/${id}`);
  }

  async reorder(id: number) {
    return this.fetch<{ items: Array<{ product_id: number; reference: string; name: string; quantity: number; current_price: number | null; available: boolean }> }>(
      `/business/orders/${id}/reorder`,
      { method: 'POST' }
    );
  }

  // Credit
  async getCredit() {
    return this.fetch<BusinessCreditOverview>('/business/credit');
  }

  async getCreditRequests() {
    return this.fetch<BusinessCreditRequestsResponse>('/business/credit-requests');
  }

  async submitCreditRequest(data: { requested_amount: number; note?: string }) {
    return this.fetch<{ id: number; status: string }>('/business/credit-requests', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getInvoices() {
    return this.fetch<{ invoices: BusinessInvoice[] }>('/business/invoices');
  }

  // Checkout
  async checkout(data: { items: Array<{ product_id: number; quantity: number; vehicle_ktype?: number; registration_plate?: string }>; shipping_address_id?: number; notes?: string; business_vehicle_id?: number; delivery_method?: 'shipping' | 'pickup' }) {
    return this.fetch<BusinessCheckoutResult>('/business/checkout', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async payWithInvoice(order_id: number) {
    return this.fetch<{ success: boolean; credit_used: number }>('/business/checkout/invoice', {
      method: 'POST',
      body: JSON.stringify({ order_id }),
    });
  }

  async payWithBambora(order_id: number) {
    return this.fetch<{ token: string; url: string }>('/business/checkout/bambora', {
      method: 'POST',
      body: JSON.stringify({ order_id }),
    });
  }

  // B2B Products
  async searchProducts(params: { q?: string; type?: string; page?: number; limit?: number; ktype?: number; category?: number; montering?: string;
    /** Filterpanelens val (samma som autofrance.se/sok) — skickas som f_* till API:t. */
    filter?: { category?: string; manufacturer?: string; brand?: string; quality?: string; express?: string; inStock?: string; sort?: string; attr?: string[] };
    /** Bestämda artiklar i given ordning (Favoriter, Köpt förut). */
    ids?: number[] }) {
    const searchParams = new URLSearchParams();
    if (params.ids?.length) searchParams.set('ids', params.ids.join(','));
    for (const [k, v] of Object.entries(params.filter ?? {})) {
      if (Array.isArray(v)) v.forEach((x) => searchParams.append(`f_${k}`, x));
      else if (v) searchParams.set(`f_${k}`, v);
    }
    if (params.q) searchParams.set('q', params.q);
    if (params.type) searchParams.set('type', params.type);
    if (params.page) searchParams.set('page', String(params.page));
    if (params.limit) searchParams.set('limit', String(params.limit));
    if (params.ktype) searchParams.set('ktype', String(params.ktype));
    if (params.category) searchParams.set('category', String(params.category));
    if (params.montering) searchParams.set('montering', params.montering);
    return this.fetch<B2BProductSearchResult>(`/business/products?${searchParams}`);
  }

  async koptForut() {
    return this.fetch<{ rader: Array<{ product_id: number; ganger: number; antal: number; senast: string }> }>('/business/kopt-forut');
  }

  async getSparadeKorgar() {
    return this.fetch<{ korgar: SparadKorg[] }>('/business/sparade-korgar');
  }

  async sparaKorg(data: { regnr?: string; namn?: string; rader: Array<{ product_id: number; quantity: number; vehicle_ktype?: number | null }> }) {
    return this.fetch<{ ok: boolean; ersatt: boolean }>('/business/sparade-korgar', { method: 'POST', body: JSON.stringify(data) });
  }

  async taBortSparadKorg(id: number) {
    return this.fetch<{ ok: boolean }>(`/business/sparade-korgar/${id}`, { method: 'DELETE' });
  }

  /** Dagens företagspris per artikel, samma uträkning som kassan debiterar. */
  async getPriser(ids: number[]) {
    return this.fetch<{ rabatt_procent?: number; prices_hidden?: boolean;
      priser: Record<string, { lista: number; pris: number; kampanj: { namn: string } | null; aktiv: boolean }> }>(
      `/business/priser?ids=${ids.join(',')}`);
  }

  async getProduct(id: number) {
    return this.fetch<B2BProductDetail>(`/business/products/${id}`);
  }

  // B2B Vehicles (garage)
  async getVehicles(params?: { status?: string; search?: string }) {
    const searchParams = new URLSearchParams();
    if (params?.status) searchParams.set('status', params.status);
    if (params?.search) searchParams.set('search', params.search);
    const query = searchParams.toString();
    return this.fetch<{ vehicles: BusinessVehicleListItem[] }>(`/business/vehicles${query ? `?${query}` : ''}`);
  }

  // Sprängskiss utan originalnummer (ägaren 2026-10-12) — länkar är token som bara gäller företaget.
  async sprangskissFordon(bil: number) {
    return this.fetch<{ bil: { namn: string; plate: string | null }; vy: SkissVy | null; grupper?: Record<string, SkissVy>; meddelande: string | null }>(`/business/sprangskiss/fordon?bil=${bil}`);
  }
  /** Artiklarna som passar exakt enligt bilens chassinummer (originalkatalogen) — bara id:n, aldrig originalnumren. */
  async sprangskissExakt(bil: number, ord: string) {
    return this.fetch<{ produkter: number[]; kalla: 'chassinummer' | null }>(`/business/sprangskiss/exakt?bil=${bil}&ord=${encodeURIComponent(ord)}`);
  }
  /** Modellbeteckningen i originalkatalogen ("Crafter 35 Enkelhytt") — 3D-bilens form. */
  async sprangskissKaross(bil: number) { return this.fetch<{ modell: string | null; register?: { kaross: string | null; dack: string | null; ar: number | null } }>(`/business/sprangskiss/kaross?bil=${bil}`); }
  /** Karossdelar till hela modellgenerationen (dörrar, luckor, speglar … beror inte på motorn). */
  async karossdelar(ktype: number) { return this.fetch<{ familj: string; produkter: Array<{ id: number; kategori: string; namn: string }> }>(`/business/sprangskiss/karossdelar?ktype=${ktype}`); }
  /** Alla delar i ett område på bilkartan (flera kategorier), bilens ktype. */
  async zonDelar(ktype: number, kat: number[]) { return this.fetch<{ produkter: number[] }>(`/business/sprangskiss/zondelar?ktype=${ktype}&kat=${kat.join(',')}`); }
  async sprangskissLank(t: string) { return this.fetch<SkissVy>(`/business/sprangskiss/lank?t=${encodeURIComponent(t)}`); }
  async sprangskissSok(t: string, q: string) { return this.fetch<SkissVy>(`/business/sprangskiss/sok?t=${encodeURIComponent(t)}&q=${encodeURIComponent(q)}`); }
  async sprangskissBild(t: string) {
    return this.fetch<{ bild: string; bredd: number; hojd: number; positioner: Array<{ pos: string; x: number; y: number; b: number; h: number }> }>(`/business/sprangskiss/bild?t=${encodeURIComponent(t)}&v=${SKISS_BILD_VERSION}`);
  }

  /** Bil vald med märke/modell/motor när regnumret inte finns i katalogerna (regnumret följer med om det är giltigt). */
  async laggTillBilManuellt(ktype: number, plate?: string) {
    return this.fetch<{ vehicle: BusinessVehicle }>('/business/vehicles/manuell', { method: 'POST', body: JSON.stringify({ ktype, plate }) });
  }

  async lookupVehicle(data: { plate?: string; vin?: string }) {
    return this.fetch<{ vehicle: BusinessVehicle; lookup: { ktype: number; manufacturer: string; model: string; cartype: string; from_year: number; to_year: number | null } }>(
      '/business/vehicles/lookup',
      { method: 'POST', body: JSON.stringify(data) }
    );
  }

  async deleteVehicle(id: number) {
    return this.fetch<{ success: boolean }>(`/business/vehicles/${id}`, { method: 'DELETE' });
  }

  async updateVehicleAlias(id: number, alias: string) {
    return this.fetch<{ success: boolean }>(`/business/vehicles/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ alias }),
    });
  }

  // Vehicle detail
  async getVehicle(id: number) {
    return this.fetch<{ vehicle: BusinessVehicleDetail }>(`/business/vehicles/${id}`);
  }

  async updateVehicle(id: number, data: { alias?: string; status?: string; mileage?: number; engine_code?: string }) {
    return this.fetch<{ success: boolean }>(`/business/vehicles/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // Vehicle notes
  async getVehicleNotes(vehicleId: number) {
    return this.fetch<{ notes: BusinessVehicleNote[] }>(`/business/vehicles/${vehicleId}/notes`);
  }

  async createVehicleNote(
    vehicleId: number,
    content: string,
    category?: string,
    parent_id?: number,
    products?: Array<{ order_item_id: number; product_name: string; product_reference?: string; quantity: number }>,
  ) {
    return this.fetch<{ note: BusinessVehicleNote }>(`/business/vehicles/${vehicleId}/notes`, {
      method: 'POST',
      body: JSON.stringify({ content, category, parent_id, products }),
    });
  }

  async updateVehicleNote(noteId: number, data: { content?: string; status?: string; category?: string }) {
    return this.fetch<{ note: BusinessVehicleNote }>(`/business/vehicles/notes/${noteId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteVehicleNote(noteId: number) {
    return this.fetch<{ success: boolean }>(`/business/vehicles/notes/${noteId}`, {
      method: 'DELETE',
    });
  }

  async addNoteProducts(noteId: number, products: Array<{ order_item_id: number; product_name: string; product_reference?: string; quantity: number }>) {
    return this.fetch<{ products: BusinessVehicleNoteProduct[] }>(`/business/vehicles/notes/${noteId}/products`, {
      method: 'POST',
      body: JSON.stringify({ products }),
    });
  }

  async removeNoteProduct(noteId: number, productId: number) {
    return this.fetch<{ success: boolean }>(`/business/vehicles/notes/${noteId}/products/${productId}`, {
      method: 'DELETE',
    });
  }

  // Vehicle orderers
  async setVehicleOrderer(vehicleId: number, data: {
    name: string; phone?: string; email?: string;
    org_number?: string; contact_person?: string; address?: string; orderer_notes?: string;
  }) {
    return this.fetch<{ orderer: BusinessVehicleOrderer }>(`/business/vehicles/${vehicleId}/orderer`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getVehicleOrderers(vehicleId: number) {
    return this.fetch<{ orderers: BusinessVehicleOrderer[] }>(`/business/vehicles/${vehicleId}/orderers`);
  }

  // Vehicle orders
  async getVehicleOrders(vehicleId: number) {
    return this.fetch<{ orders: BusinessVehicleOrder[] }>(`/business/vehicles/${vehicleId}/orders`);
  }

  // Mechanic roster (admin manages approved mechanics)
  async getMechanics() {
    return this.fetch<{ mechanics: BusinessMechanic[] }>('/business/mechanics');
  }

  async addMechanic(data: { name: string; phone?: string; email?: string }) {
    return this.fetch<{ mechanic: BusinessMechanic }>('/business/mechanics', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateMechanic(id: number, data: { name?: string; phone?: string; email?: string; is_active?: boolean }) {
    return this.fetch<{ mechanic: BusinessMechanic }>(`/business/mechanics/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteMechanic(id: number) {
    return this.fetch<{ success: boolean }>(`/business/mechanics/${id}`, { method: 'DELETE' });
  }

  // Vehicle mechanics (assign from roster)
  async setVehicleMechanic(vehicleId: number, data: { business_mechanic_id: number }) {
    return this.fetch<{ mechanic: BusinessVehicleMechanic }>(`/business/vehicles/${vehicleId}/mechanic`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getVehicleMechanics(vehicleId: number) {
    return this.fetch<{ mechanics: BusinessVehicleMechanic[] }>(`/business/vehicles/${vehicleId}/mechanics`);
  }

  async removeVehicleMechanic(vehicleId: number, assignmentId: number) {
    return this.fetch<{ success: boolean }>(`/business/vehicles/${vehicleId}/mechanic/${assignmentId}`, {
      method: 'DELETE',
    });
  }

  // Terms acceptance
  async acceptTerms() {
    return this.fetch<{ success: boolean; accepted_at: string }>('/business/accept-terms', { method: 'POST' });
  }

  // Vehicle images
  async uploadVehicleImages(vehicleId: number, formData: FormData) {
    const token = this.getToken();
    const res = await fetch(`${API_URL}/business/vehicles/${vehicleId}/images`, {
      method: 'POST',
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: formData,
    });
    if (!res.ok) {
      const error = await res.json().catch(() => ({ error: 'Okänt fel' }));
      throw new Error(error.error || error.message || 'Kunde inte ladda upp bilder');
    }
    return res.json() as Promise<{ images: BusinessVehicleImage[] }>;
  }

  async getVehicleImages(vehicleId: number) {
    return this.fetch<{ images: BusinessVehicleImage[] }>(`/business/vehicles/${vehicleId}/images`);
  }

  async deleteVehicleImage(imageId: number) {
    return this.fetch<{ success: boolean }>(`/business/vehicles/images/${imageId}`, { method: 'DELETE' });
  }

  // Vehicle history (for print)
  async getVehicleHistory(vehicleId: number) {
    return this.fetch<BusinessVehicleHistory>(`/business/vehicles/${vehicleId}/history`);
  }

  // Invoices (enhanced)
  async getInvoicesPdf(id: number): Promise<string> {
    const token = this.getToken();
    return `${API_URL}/business/invoices/${id}/pdf${token ? `?token=${token}` : ''}`;
  }

  async payInvoice(id: number) {
    return this.fetch<{ token: string; url: string }>(`/business/invoices/${id}/pay`, {
      method: 'POST',
    });
  }

  // Fakturorna ur Fortnox — de kunden faktiskt fått (2026-10-08).
  async getFortnoxFakturor(farsk = false) {
    return this.fetch<{ fakturor: FortnoxFaktura[]; ersatta: Array<{ af_nummer: string; af_id: number; ersatt_av: number }> }>(`/business/fortnox-fakturor${farsk ? '?farsk=1' : ''}`);
  }

  async payFortnoxFaktura(nr: number) {
    return this.fetch<{ token: string; url: string; belopp: number }>(`/business/fortnox-fakturor/${nr}/pay`, { method: 'POST' });
  }

  // Stats
  async getStats() {
    return this.fetch<BusinessStats>('/business/stats');
  }

  // Audit log
  async getAuditLog(params?: { page?: number; action?: string; search?: string }) {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set('page', String(params.page));
    if (params?.action) searchParams.set('action', params.action);
    if (params?.search) searchParams.set('search', params.search);
    const query = searchParams.toString();
    return this.fetch<{
      entries: BusinessAuditEntry[];
      total: number;
      page: number;
      total_pages: number;
      chain_valid: boolean;
    }>(`/business/audit-log${query ? `?${query}` : ''}`);
  }

  // Credit notes
  async getInvoiceCreditNotes(invoiceId: number) {
    return this.fetch<{ credit_notes: BusinessCreditNote[] }>(`/business/invoices/${invoiceId}/credit-notes`);
  }

  getCreditNotePdfUrl(id: number): string {
    const token = this.getToken();
    return `${API_URL}/business/credit-notes/${id}/pdf${token ? `?token=${token}` : ''}`;
  }

  // Session timeout
  async updateSessionTimeout(timeout_minutes: number) {
    return this.fetch<{ success: boolean; session_timeout_minutes: number }>('/business/settings/timeout', {
      method: 'PUT',
      body: JSON.stringify({ timeout_minutes }),
    });
  }

  // Push notifications
  async getVapidPublicKey() {
    return this.fetch<{ key: string }>('/business/push/vapid-public-key');
  }

  async subscribePush(subscription: { endpoint: string; keys: { p256dh: string; auth: string } }) {
    return this.fetch<{ success: boolean }>('/business/push/subscribe', {
      method: 'POST',
      body: JSON.stringify(subscription),
    });
  }

  async unsubscribePush(endpoint: string) {
    return this.fetch<{ success: boolean }>('/business/push/unsubscribe', {
      method: 'POST',
      body: JSON.stringify({ endpoint }),
    });
  }

  async getPushStatus() {
    return this.fetch<{ subscribed: boolean }>('/business/push/status');
  }

  // Part Requests
  async createPartRequest(formData: FormData): Promise<{ id: number; ticket_number: string; status: string }> {
    const token = this.getToken();
    const res = await fetch(`${API_URL}/part-requests`, {
      method: 'POST',
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: formData,
    });
    if (!res.ok) {
      const error = await res.json().catch(() => ({ message: 'Okänt fel' }));
      throw new Error(error.message || 'Kunde inte skapa förfrågan');
    }
    return res.json();
  }

  async getMyPartRequests(page = 1) {
    return this.fetch<{ data: unknown[]; total: number; page: number; total_pages: number }>(
      `/part-requests/my?page=${page}`
    );
  }

  async getMyPartRequest(id: number) {
    return this.fetch<unknown>(`/part-requests/my/${id}`);
  }

  async sendPartRequestMessage(id: number, formData: FormData): Promise<unknown> {
    const token = this.getToken();
    const res = await fetch(`${API_URL}/part-requests/my/${id}/message`, {
      method: 'POST',
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: formData,
    });
    if (!res.ok) {
      const error = await res.json().catch(() => ({ message: 'Okänt fel' }));
      throw new Error(error.message || 'Kunde inte skicka meddelande');
    }
    return res.json();
  }

  async acceptPartRequest(id: number, paymentMethod = 'invoice') {
    return this.fetch<{ order_id: number; reference: string; payment_method: string; payment_url?: string }>(
      `/part-requests/my/${id}/accept`,
      { method: 'POST', body: JSON.stringify({ payment_method: paymentMethod }) }
    );
  }

  async declinePartRequest(id: number, reason?: string) {
    return this.fetch<{ success: boolean }>(`/part-requests/my/${id}/decline`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });
  }

  async expeditePartRequest(id: number) {
    return this.fetch<{ success: boolean }>(`/part-requests/my/${id}/expedite`, { method: 'POST' });
  }

  async cancelPartRequest(id: number) {
    return this.fetch<{ success: boolean }>(`/part-requests/my/${id}/cancel`, { method: 'POST' });
  }

  async getPartRequestUnreadCount() {
    return this.fetch<{ count: number }>('/part-requests/my/unread-count');
  }

  async getReviewCredits() {
    return this.fetch<BusinessReviewCreditProgress>('/business/me/review-credits');
  }

  // Cars
  async getCarTermsStatus() {
    return this.fetch<CarTermsStatus>('/business/cars/terms/status');
  }

  async acceptCarTerms() {
    return this.fetch<{ success: boolean }>('/business/cars/terms/accept', { method: 'POST' });
  }

  async getCars(filters?: { brand?: string; fuel_type?: string; min_price?: string; max_price?: string }) {
    const sp = new URLSearchParams();
    if (filters?.brand) sp.set('brand', filters.brand);
    if (filters?.fuel_type) sp.set('fuel_type', filters.fuel_type);
    if (filters?.min_price) sp.set('min_price', filters.min_price);
    if (filters?.max_price) sp.set('max_price', filters.max_price);
    const q = sp.toString();
    return this.fetch<B2BCar[]>(`/business/cars${q ? `?${q}` : ''}`);
  }

  async getCar(id: number) {
    return this.fetch<B2BCarDetail>(`/business/cars/${id}`);
  }

  async placeBid(carId: number, amount: number) {
    return this.fetch<{ bid: B2BCarBid; auction_extended: string | null }>(`/business/cars/${carId}/bid`, {
      method: 'POST', body: JSON.stringify({ amount }),
    });
  }

  async buyNow(carId: number) {
    return this.fetch<{ order: B2BCarOrder }>(`/business/cars/${carId}/buy-now`, { method: 'POST' });
  }

  async getMyCarBids() {
    return this.fetch<(B2BCarBid & { car: { id: number; brand: string | null; model: string | null; year: number | null; status: string; auction_end: string | null } })[]>('/business/cars/my-bids');
  }

  async getCarOrders() {
    return this.fetch<B2BCarOrder[]>('/business/cars/orders');
  }

  async getCarOrder(id: number) {
    return this.fetch<B2BCarOrder>(`/business/cars/orders/${id}`);
  }

  // ─── Returns ────────────────────────────────────────────────────────────

  async getReturns(params?: { page?: number; status?: string }) {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set('page', String(params.page));
    if (params?.status) searchParams.set('status', params.status);
    const query = searchParams.toString();
    return this.fetch<{ data: ReturnSummary[]; total: number; page: number; total_pages: number }>(
      `/business/returns${query ? `?${query}` : ''}`
    );
  }

  async getReturn(id: number) {
    return this.fetch<ReturnDetail>(`/business/returns/${id}`);
  }

  async getReturnableItems(orderId: number) {
    return this.fetch<{ order_reference: string; items: ReturnableItem[] }>(
      `/business/returns/returnable/${orderId}`
    );
  }

  async createReturn(data: {
    order_id: number;
    type: 'return' | 'defective';
    reason?: string;
    items: Array<{ order_item_id: number; quantity: number; reason?: string }>;
  }) {
    return this.fetch<{ id: number; return_number: string; status: string; refund_amount: number; admin_fee_percent: number }>(
      '/business/returns',
      { method: 'POST', body: JSON.stringify(data) }
    );
  }

  async addReturnTracking(id: number, data: { tracking_number: string; carrier?: string }) {
    return this.fetch<{ success: boolean }>(`/business/returns/${id}/tracking`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // ─── Mechanic login/registration ─────────────────────────

  async inviteMechanic(mechanicId: number) {
    return this.fetch<{ success: boolean; message: string }>(`/business/mechanics/${mechanicId}/invite`, { method: 'POST' });
  }

  async mechanicRegister(data: { token: string; firstname: string; lastname: string; phone?: string; password: string; accept_terms: boolean }) {
    const res = await fetch(`${API_URL}/business/mechanic-register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!res.ok) {
      const error = await res.json().catch(() => ({ error: 'Okänt fel' }));
      throw new Error(error.error || 'Registrering misslyckades');
    }
    return res.json() as Promise<BusinessLoginResponse>;
  }

  async mechanicAcceptTerms() {
    return this.fetch<{ success: boolean }>('/business/mechanic-accept-terms', { method: 'POST' });
  }

  async getMechanicVehicles() {
    return this.fetch<{ vehicles: BusinessVehicle[] }>('/business/mechanic/vehicles');
  }

  async getMechanicOrders(page = 1) {
    return this.fetch<{ orders: BusinessVehicleOrder[]; total: number }>(`/business/mechanic/orders?page=${page}`);
  }

  async setMechanicSeePrices(value: boolean) {
    return this.fetch<{ success: boolean }>('/business/settings/mechanic-prices', {
      method: 'PUT',
      body: JSON.stringify({ mechanic_see_prices: value }),
    });
  }

  async setMechanicPermissions(data: { mechanic_see_prices?: boolean; mechanic_see_cart?: boolean; mechanic_can_order?: boolean }) {
    return this.fetch<{ success: boolean }>('/business/settings/mechanic-permissions', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async approveOrder(orderId: number) {
    return this.fetch<{ success: boolean }>(`/business/orders/${orderId}/approve`, { method: 'POST' });
  }

  async rejectOrder(orderId: number) {
    return this.fetch<{ success: boolean }>(`/business/orders/${orderId}/reject`, { method: 'POST' });
  }

  async cancelOrder(orderId: number) {
    return this.fetch<{ success: boolean; refund_amount: number; fee_amount: number; fee_percent: number }>(`/business/orders/${orderId}/cancel`, { method: 'POST' });
  }

  async getVehicleActivity(vehicleId: number, limit = 50, offset = 0) {
    return this.fetch<{ activities: import('@/types/business').BusinessVehicleActivity[] }>(`/business/vehicles/${vehicleId}/activity?limit=${limit}&offset=${offset}`);
  }

  async getPendingFees() {
    return this.fetch<{ fees: import('@/types/business').BusinessPendingFee[] }>('/business/pending-fees');
  }

  async updateCancelFeeBilling(cancel_fee_billing: string) {
    return this.fetch<{ success: boolean }>('/business/settings/cancel-fee-billing', {
      method: 'PUT',
      body: JSON.stringify({ cancel_fee_billing }),
    });
  }

  async updateBillingMode(billing_mode: string) {
    return this.fetch<{ success: boolean }>('/business/settings/billing-mode', {
      method: 'PUT',
      body: JSON.stringify({ billing_mode }),
    });
  }

  // ═══════════════════════════════════════════════════════
  // Insurance Portal
  // ═══════════════════════════════════════════════════════

  // ─── Workshops ───────────────────────────────────────
  // ─── Verkstadskartan (2026-10-07) ───
  async getKartVerkstader() {
    return this.fetch<{ verkstader: Array<Record<string, unknown> & { id: string; typ: 'portal' | 'egen'; namn: string; lat: number | null; lon: number | null }> }>('/business/insurance/karta/verkstader');
  }
  async getNarmasteVerkstader(adress: string) {
    return this.fetch<{ punkt: { lat: number; lon: number }; verkstader: Array<Record<string, unknown> & { id: string; typ: 'portal' | 'egen'; namn: string; km: number; kan_tilldelas: boolean; workshop_customer_id: number | null }> }>(
      `/business/insurance/karta/narmast?adress=${encodeURIComponent(adress)}`);
  }
  async addKartVerkstad(data: Record<string, string>) {
    return this.fetch<{ id: number; hittad: boolean }>('/business/insurance/karta/verkstader', { method: 'POST', body: JSON.stringify(data) });
  }
  async deleteKartVerkstad(id: number) {
    return this.fetch<{ ok: boolean }>(`/business/insurance/karta/verkstader/${id}`, { method: 'DELETE' });
  }

  // ─── Roller, team, handläggare, attest och logg (2026-10-07) ───
  async getInsuranceMe() {
    return this.fetch<PortalMe>('/business/insurance/me');
  }
  async getInsuranceTeam() {
    return this.fetch<{ team: PortalTeamMedlem[]; roller: Array<{ roll: string; namn: string; standard_attest: number | null }> }>('/business/insurance/team');
  }
  async createInsuranceTeamUser(data: { email: string; firstname: string; lastname: string; phone?: string; roll: string; attest_grans?: number | null }) {
    return this.fetch<{ id: number }>('/business/insurance/team', { method: 'POST', body: JSON.stringify(data) });
  }
  async updateInsuranceTeamUser(id: number, data: { roll?: string; attest_grans?: number | null; active?: boolean }) {
    return this.fetch<{ ok: boolean }>(`/business/insurance/team/${id}`, { method: 'PUT', body: JSON.stringify(data) });
  }
  async setClaimHandlaggare(claimId: number, handlaggare_id: number) {
    return this.fetch<{ ok: boolean }>(`/business/insurance/claims/${claimId}/handlaggare`, { method: 'PUT', body: JSON.stringify({ handlaggare_id }) });
  }
  async begarAttest(claimId: number, till: number) {
    return this.fetch<{ ok: boolean; till: string }>(`/business/insurance/claims/${claimId}/begar-attest`, { method: 'POST', body: JSON.stringify({ till }) });
  }
  async getInsuranceLogg(params: Record<string, string>) {
    return this.fetch<{ rader: PortalLoggRad[]; sida: number; kedjan_hel: boolean | null }>(`/business/insurance/logg?${new URLSearchParams(params)}`);
  }
  /** Loggen som CSV — hämtas med inloggningen och sparas som fil. */
  async exportInsuranceLogg(params: Record<string, string>) {
    const token = this.getToken();
    const res = await fetch(`${API_URL}/business/insurance/logg/export?${new URLSearchParams(params)}`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
    if (!res.ok) throw new Error('Kunde inte exportera loggen');
    return res.blob();
  }

  async getInsuranceWorkshops() {
    return this.fetch<{ workshops: InsuranceApprovedWorkshop[] }>('/business/insurance/workshops');
  }

  async addInsuranceWorkshop(data: { workshop_customer_id: number; contact_name?: string; contact_email?: string; contact_phone?: string; notes?: string }) {
    return this.fetch<{ message: string }>('/business/insurance/workshops', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateInsuranceWorkshop(id: number, data: Record<string, unknown>) {
    return this.fetch<{ message: string }>(`/business/insurance/workshops/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async removeInsuranceWorkshop(id: number) {
    return this.fetch<{ message: string }>(`/business/insurance/workshops/${id}`, {
      method: 'DELETE',
    });
  }

  async searchInsuranceWorkshops(q: string) {
    return this.fetch<{ results: InsuranceWorkshopSearchResult[] }>(`/business/insurance/workshops/search?q=${encodeURIComponent(q)}`);
  }

  async inviteWorkshop(data: { company_name: string; org_number: string; email: string; contact_name?: string }) {
    return this.fetch<{ message: string; linked?: boolean; pending?: boolean }>('/business/insurance/workshops/invite', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getWorkshopInvitations() {
    return this.fetch<{ invitations: InsuranceWorkshopInvitation[] }>('/business/insurance/workshops/invitations');
  }

  async cancelWorkshopInvitation(id: number) {
    return this.fetch<{ message: string }>(`/business/insurance/workshops/invitations/${id}`, {
      method: 'DELETE',
    });
  }

  // ─── Claims ──────────────────────────────────────────
  async getInsuranceClaims(params?: { status?: string; workshop_id?: number; page?: number; limit?: number; mina?: boolean; attest?: boolean; q?: string; handlaggare?: number }) {
    const qs = new URLSearchParams();
    if (params?.status) qs.set('status', params.status);
    if (params?.mina) qs.set('mina', '1');
    if (params?.attest) qs.set('attest', '1');
    if (params?.q) qs.set('q', params.q);
    if (params?.handlaggare) qs.set('handlaggare', String(params.handlaggare));
    if (params?.workshop_id) qs.set('workshop_id', String(params.workshop_id));
    if (params?.page) qs.set('page', String(params.page));
    if (params?.limit) qs.set('limit', String(params.limit));
    return this.fetch<{ claims: InsuranceClaimSummary[]; total: number; page: number; total_pages: number }>(
      `/business/insurance/claims?${qs}`
    );
  }

  async createInsuranceClaim(data: Record<string, unknown>) {
    return this.fetch<{ id: number; claim_number: string }>('/business/insurance/claims', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getInsuranceClaim(id: number) {
    return this.fetch<{
      claim: InsuranceClaimDetail;
      items: InsuranceClaimItem[];
      labor: InsuranceClaimLabor[];
      documents: InsuranceClaimDocument[];
      history: InsuranceStatusHistoryEntry[];
    }>(`/business/insurance/claims/${id}`);
  }

  async updateInsuranceClaim(id: number, data: Record<string, unknown>) {
    return this.fetch<{ message: string }>(`/business/insurance/claims/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async assignClaimWorkshop(claimId: number, workshop_customer_id: number) {
    return this.fetch<{ message: string }>(`/business/insurance/claims/${claimId}/assign`, {
      method: 'POST',
      body: JSON.stringify({ workshop_customer_id }),
    });
  }

  async approveClaim(claimId: number, comment?: string) {
    return this.fetch<{ message: string; order_id: number | null }>(`/business/insurance/claims/${claimId}/approve`, {
      method: 'POST',
      body: JSON.stringify({ comment }),
    });
  }

  async rejectClaim(claimId: number, reason: string) {
    return this.fetch<{ message: string }>(`/business/insurance/claims/${claimId}/reject`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });
  }

  async cancelClaim(claimId: number, reason?: string) {
    return this.fetch<{ message: string }>(`/business/insurance/claims/${claimId}/cancel`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });
  }

  async completeClaim(claimId: number) {
    return this.fetch<{ message: string }>(`/business/insurance/claims/${claimId}/complete`, {
      method: 'POST',
    });
  }

  // ─── Documents ───────────────────────────────────────
  async getClaimDocuments(claimId: number) {
    return this.fetch<{ documents: InsuranceClaimDocument[] }>(`/business/insurance/claims/${claimId}/documents`);
  }

  async uploadClaimDocuments(claimId: number, formData: FormData, isWorkshop = false) {
    const token = this.getToken();
    const prefix = isWorkshop ? '/business/insurance/workshop' : '/business/insurance';
    const res = await fetch(`${API_URL}${prefix}/claims/${claimId}/documents`, {
      method: 'POST',
      headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: formData,
    });
    if (!res.ok) {
      const error = await res.json().catch(() => ({ message: 'Okänt fel' }));
      throw new Error(error.message || 'Kunde inte ladda upp dokument');
    }
    return res.json() as Promise<{ message: string }>;
  }

  async deleteClaimDocument(docId: number) {
    return this.fetch<{ message: string }>(`/business/insurance/claims/documents/${docId}`, {
      method: 'DELETE',
    });
  }

  // ─── Billing Profile ─────────────────────────────────
  async getInsuranceBillingProfile() {
    return this.fetch<{ profile: InsuranceBillingProfile | null }>('/business/insurance/billing-profile');
  }

  async updateInsuranceBillingProfile(data: Record<string, unknown>) {
    return this.fetch<{ message: string }>('/business/insurance/billing-profile', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // ─── Labor Invoices (insurance receives) ─────────────
  async getInsuranceLaborInvoices() {
    return this.fetch<{ invoices: InsuranceLaborInvoice[] }>('/business/insurance/labor-invoices');
  }

  async markLaborInvoicePaid(id: number) {
    return this.fetch<{ message: string }>(`/business/insurance/labor-invoices/${id}/mark-paid`, {
      method: 'POST',
    });
  }

  // ─── Stats ───────────────────────────────────────────
  async getInsuranceStats() {
    return this.fetch<InsuranceStats>('/business/insurance/stats');
  }

  // ─── Reports ──────────────────────────────────────────
  async getInsuranceReports(params?: { from?: string; to?: string }) {
    const qs = new URLSearchParams();
    if (params?.from) qs.set('from', params.from);
    if (params?.to) qs.set('to', params.to);
    return this.fetch<InsuranceReportData>(`/business/insurance/reports?${qs}`);
  }

  // ═══════════════════════════════════════════════════════
  // Workshop Insurance Endpoints
  // ═══════════════════════════════════════════════════════

  async getWorkshopClaims() {
    return this.fetch<{ claims: InsuranceClaimSummary[] }>('/business/insurance/workshop/claims');
  }

  // Verkstaden aktiverar själv försäkringsärenden (ägaren 2026-10-12) — bara administratören får ändra.
  async getForsakringAktivering() {
    return this.fetch<ForsakringAktivering>('/business/insurance/workshop/aktivering');
  }

  async sattForsakringAktivering(aktiv: boolean) {
    return this.fetch<ForsakringAktivering>('/business/insurance/workshop/aktivering', { method: 'POST', body: JSON.stringify({ aktiv, bekraftat: aktiv }) });
  }

  async getWorkshopClaim(id: number) {
    return this.fetch<{
      claim: InsuranceClaimDetail;
      items: InsuranceClaimItem[];
      labor: InsuranceClaimLabor[];
      documents: InsuranceClaimDocument[];
      history: InsuranceStatusHistoryEntry[];
    }>(`/business/insurance/workshop/claims/${id}`);
  }

  async workshopStartAssessment(claimId: number) {
    return this.fetch<{ message: string }>(`/business/insurance/workshop/claims/${claimId}/start-assessment`, {
      method: 'POST',
    });
  }

  async workshopAddItem(claimId: number, data: { product_id?: number; product_reference?: string; product_name?: string; quantity?: number; workshop_note?: string }) {
    return this.fetch<{ id: number }>(`/business/insurance/workshop/claims/${claimId}/items`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async workshopUpdateItem(claimId: number, itemId: number, data: { quantity?: number; workshop_note?: string }) {
    return this.fetch<{ message: string }>(`/business/insurance/workshop/claims/${claimId}/items/${itemId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async workshopDeleteItem(claimId: number, itemId: number) {
    return this.fetch<{ message: string }>(`/business/insurance/workshop/claims/${claimId}/items/${itemId}`, {
      method: 'DELETE',
    });
  }

  async workshopAddLabor(claimId: number, data: { description: string; hours?: number; hourly_rate?: number; total_cost?: number; category?: string }) {
    return this.fetch<{ id: number }>(`/business/insurance/workshop/claims/${claimId}/labor`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async workshopUpdateLabor(claimId: number, laborId: number, data: Record<string, unknown>) {
    return this.fetch<{ message: string }>(`/business/insurance/workshop/claims/${claimId}/labor/${laborId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async workshopDeleteLabor(claimId: number, laborId: number) {
    return this.fetch<{ message: string }>(`/business/insurance/workshop/claims/${claimId}/labor/${laborId}`, {
      method: 'DELETE',
    });
  }

  async workshopSubmitAssessment(claimId: number) {
    return this.fetch<{ message: string }>(`/business/insurance/workshop/claims/${claimId}/submit-assessment`, {
      method: 'POST',
    });
  }

  async workshopStartRepair(claimId: number) {
    return this.fetch<{ message: string }>(`/business/insurance/workshop/claims/${claimId}/start-repair`, {
      method: 'POST',
    });
  }

  async workshopCompleteRepair(claimId: number) {
    return this.fetch<{ message: string }>(`/business/insurance/workshop/claims/${claimId}/complete-repair`, {
      method: 'POST',
    });
  }

  async workshopGetBillingInfo(claimId: number) {
    return this.fetch<{ billing_info: (InsuranceBillingProfile & { company_name: string; org_number: string; contact_email: string }) | null }>(
      `/business/insurance/workshop/claims/${claimId}/billing-info`
    );
  }

  async workshopCreateLaborInvoice(claimId: number, notes?: string) {
    return this.fetch<{ invoice_number: string; amount_excl_vat: number; vat_amount: number; total: number }>(
      `/business/insurance/workshop/claims/${claimId}/labor-invoice`,
      { method: 'POST', body: JSON.stringify({ notes }) }
    );
  }

  async workshopGetLaborInvoices() {
    return this.fetch<{ invoices: InsuranceLaborInvoice[] }>('/business/insurance/workshop/labor-invoices');
  }

  // ─── Partner Car Listings ───────────────────────────────────────────────

  async getPartnerListings(params?: {
    search?: string; make?: string; fuel?: string; transmission?: string;
    min_price?: number; max_price?: number; min_year?: number; max_year?: number;
    max_mileage?: number; sort?: string;
  }) {
    const sp = new URLSearchParams();
    if (params) {
      for (const [k, v] of Object.entries(params)) {
        if (v !== undefined && v !== '' && v !== null) sp.set(k, String(v));
      }
    }
    const qs = sp.toString();
    return this.fetch<PartnerListingsResponse>(`/business/blocket/listings${qs ? `?${qs}` : ''}`);
  }

  async getPartnerListing(id: number) {
    return this.fetch<PartnerListingDetail>(`/business/blocket/listings/${id}`);
  }

  async placePartnerBid(listingId: number, data: {
    bid_amount?: number; message?: string; bid_type?: string;
    contact_name?: string; contact_email?: string; contact_phone?: string; contact_address?: string;
  }) {
    return this.fetch<PartnerBidResponse>(`/business/blocket/listings/${listingId}/bid`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getMyPartnerBids() {
    return this.fetch<PartnerMyBid[]>('/business/blocket/my-bids');
  }
}

export const businessApi = new BusinessAPI();

// ─── Return Types ──────────────────────────────────────────────────────────

export interface ReturnSummary {
  id: number;
  return_number: string;
  order_id: number;
  order_reference: string;
  type: string;
  status: string;
  refund_amount: number | null;
  admin_fee_percent: number | null;
  created_at: string;
}

export interface ReturnDetail {
  id: number;
  return_number: string;
  order_id: number;
  order_reference: string;
  type: string;
  status: string;
  reason: string | null;
  admin_note: string | null;
  tracking_number: string | null;
  carrier: string | null;
  admin_fee_percent: number | null;
  refund_amount: number | null;
  credit_note_number: string | null;
  created_at: string;
  updated_at: string;
  items: ReturnItemDetail[];
}

export interface ReturnItemDetail {
  id: number;
  order_item_id: number;
  quantity: number;
  reason: string | null;
  reference: string;
  name: string;
  unit_price: number;
}

export interface ReturnableItem {
  id: number;
  product_id: number;
  reference: string;
  name: string;
  quantity: number;
  unit_price: number;
  total_price: number;
  returned_qty: number;
  returnable_qty: number;
}

export interface BusinessReviewCreditProgress {
  review_count: number;
  balance: number;
  total_earned: number;
  total_used: number;
  next_milestone: {
    review_count: number;
    credit_amount: number;
    reviews_remaining: number;
  } | null;
  history: Array<{
    id: number;
    amount: number;
    balance_after: number;
    type: string;
    description: string | null;
    created_at: string;
  }>;
}

/** En sparad varukorg — bara artiklar och antal; priset räknas när den hämtas. */
export interface SparadKorg {
  id: number; regnr: string | null; namn: string; sparad: string; sparad_av: string;
  rader: Array<{ product_id: number; quantity: number; vehicle_ktype: number | null }>;
}
